import React, { useState, useEffect, useMemo } from "react";
import { Button, Divider, Icon } from "semantic-ui-react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { importUpdateTransactions } from "../../../../../redux/actions/conciliacion";
import { formatMoneyWithCurrency } from "../../../../../utils/formatter";
import {
  getReconciledAmount,
  hasPendingReconciliation,
  isFullyReconciled,
} from "../../../../../utils/reconciliationProgress";
import moment from "moment";
// Fetch ALL match types for a transaction (documents, transactions, manual, clay)
import { FindMatchsByTransactionId } from "../../../../../graphql/conciliacion";
// Fetch documents for match docIds in a single query
import { FindDocumentById } from "../../../../../graphql/documents";
import { GetMatchRecommendationsByTransaction } from "../../../../../graphql/matchRecommender";
import TransactionInfoBar from "./transactionInfoBar";
import VincularDocumentoModal from "./VincularDocumentoModal";
import CreateObligationModal from "../../../../configuracion/createObligationModal/createObligationModal";
import MatchList from "./components/MatchList";
import DocumentActionsDropdown from "./components/DocumentActionsDropdown";
import CustomModal from "../../../../configuracion/components/customModal";
import UndoAllMatchesModalContent from "./components/UndoAllMatchesModalContent";
import { useUndoAllMatches } from "./hooks/useUndoAllMatches";
import BulkUnmatchProgress from "./BulkUnmatchProgress";
import { getDocumentLabelWithNumber, getName, getMatchUserInfo } from "./components/matchRowHelpers";
import { banksFormat } from "../../../../../utils/bankList";
import PossibleDuplicateReviewControls from "./PossibleDuplicateReviewModal";
import ReportDocumentButton from "../../../../shared/ReportDocumentButton/ReportDocumentButton";
import { useBlockedFeatures } from "src/hooks/useBlockedFeatures";
import { buildUnmatchInput } from "./utils/unmatchInput";
import { useStatementUnmatchGuard } from "./hooks/useStatementUnmatchGuard";
import StatementLinkedUnmatchModal from "./components/StatementLinkedUnmatchModal";
import "./style.scss";
import "./reconcileComments.css";
import { dt } from "src/utils/documentsI18n";

/**
 * ReconcileSideBar - Simplified reconcile component
 * Uses MatchList component for displaying matches
 */
const ReconcileSideBar = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { openSidebar } = useSelector((state) => state.openSidebar);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { updateTransactions } = useSelector((state) => state.conciliacion);
  const { editData, options } = openSidebar;
  const isDocument = options?.type == "documents";

  const { isConciliacionBlocked, isCrearObligacionBlocked } = useBlockedFeatures();
  const isReadOnly = isConciliacionBlocked();
  const crearObligacionBlocked = isCrearObligacionBlocked();

  // Modal states
  const [showVincularModal, setShowVincularModal] = useState(false);
  const [showObligationModal, setShowObligationModal] = useState(false);
  const [showUndoAllModal, setShowUndoAllModal] = useState(false);

  // Fetch ALL matches for this transaction from the server (documents, transactions, manual, clay)
  // This is the authoritative source that finds matches in all directions
  const { data: serverMatchData, fetching: matchesFetching, reexecuteQuery: refetchMatches } = FindMatchsByTransactionId({
    transactionId: editData?.id || editData?._id,
    dte: isDocument,
  });

  // Use editData.matchs for enrichment (candidates, matchContainer) but server data for display
  const matchesFromTransaction = editData?.matchs || [];
  
  // Get strong and weak match candidates (only for transactions, not documents)
  // Check both editData from Redux and data from reconcile.jsx to ensure we get candidates
  // NOTA: aiRecommendations y systemRecommendations ya no se usan - ahora todo viene de matchRecommendations (Zona de Entrenamiento)
  const transactionData = editData || {};
  const strongMatchCandidates = !isDocument && transactionData?.strongMatchCandidates ? transactionData.strongMatchCandidates : [];
  const weakMatchCandidates = !isDocument && transactionData?.weakMatchCandidates ? transactionData.weakMatchCandidates : [];

  // Fetch Match Recommender recommendations for this transaction (solo las existentes en BD)
  // IMPORTANTE: Usar _id (MongoDB) en lugar de id (que puede ser un ID sintético de urql cache)
  // El target_transaction_id en BD se guarda como string del _id de MongoDB
  const rawTxId = !isDocument ? (transactionData?._id ?? transactionData?.id) : null;
  const transactionId = rawTxId != null
    ? (typeof rawTxId === 'string' ? rawTxId : (typeof rawTxId?.toString === 'function' ? rawTxId.toString() : String(rawTxId)))
    : null;

  const matchsLength = editData?.matchs?.length || 0;
  // Refetch server matches when matchs cambia (incl. 0 tras unmatch) para no dejar datos obsoletos en UI
  useEffect(() => {
    if (transactionId) {
      refetchMatches({ requestPolicy: 'network-only' });
    }
  }, [matchsLength, transactionId, refetchMatches]);

  useEffect(() => {
    if (updateTransactions === true) {
      refetchMatches({ requestPolicy: 'network-only' });
      dispatch(importUpdateTransactions(false));
    }
  }, [updateTransactions, dispatch, refetchMatches]);

  const companyId = selectedCompany?.id;
  const { data: matchRecData, reexecuteQuery: refetchMatchRec } = GetMatchRecommendationsByTransaction(companyId, transactionId);
  const matchRecommendationsFromQuery = matchRecData?.matchRecommendations?.items || [];

  // Si la transacción trae aiRecommendations (resueltas desde match_recommendations por target_transaction_id),
  // usarlas como fuente; pero preferir la recomendación completa de la query cuando exista (mismo id), para que
  // en el modal de edición se muestren detected_pattern, stats, recommendation_reason, etc.
  const matchRecommendations = useMemo(() => {
    const fromTx = editData?.aiRecommendations || [];
    if (fromTx.length > 0) {
      return fromTx.map((ar) => {
        const full = matchRecommendationsFromQuery.find(
          (r) => String(r.id) === String(ar.recommendationId)
        );
        if (full) return full;
        return {
          id: ar.recommendationId,
          obligation_suggestion: {
            obligation_type: ar.obligationType,
            obligation_type_label: ar.obligationTypeLabel,
            suggested_description: ar.suggestedDescription,
            suggested_amount: ar.suggestedAmount,
          },
          target_transaction: {
            amount: editData?.amount,
            description: editData?.description,
            type: editData?.type,
            date: editData?.date,
          },
          company_id: editData?.companyId || companyId,
          status: 'pending',
          type: ar.recommendationType || 'CREATE_OBLIGATION',
          suggested_doc_id: ar.suggestedDocId,
        };
      });
    }
    return matchRecommendationsFromQuery;
  }, [editData?.aiRecommendations, editData?.amount, editData?.description, editData?.type, editData?.date, editData?.companyId, companyId, matchRecommendationsFromQuery]);

  // Extract applied_obligation_ids from match recommendations (desde la query, donde sí viene applied_obligation_id)
  const appliedObligationIds = useMemo(() => {
    return matchRecommendationsFromQuery
      .filter(rec => rec.applied_obligation_id)
      .map(rec => rec.applied_obligation_id);
  }, [matchRecommendationsFromQuery]);

  // Combine all document IDs (matches + strong candidates + weak candidates + applied obligations)
  // This ensures we fetch all documents in a single API call, including candidate documents
  const docIds = useMemo(() => {
    const matchDocIds = matchesFromTransaction.map(m => m.docId).filter(Boolean);
    const strongCandidateDocIds = strongMatchCandidates.map(c => c?.documentId).filter(Boolean);
    const weakCandidateDocIds = weakMatchCandidates.map(c => c?.documentId).filter(Boolean);
    // Include applied_obligation_ids from match recommendations (AI-created obligations)
    return [...new Set([...matchDocIds, ...strongCandidateDocIds, ...weakCandidateDocIds, ...appliedObligationIds])]; // Remove duplicates
  }, [matchesFromTransaction, strongMatchCandidates, weakMatchCandidates, appliedObligationIds]);
  
  // Create variables object - always create a new reference to ensure urql detects changes
  const findDocumentVariables = useMemo(() => {
    return { ids: docIds.length > 0 ? [...docIds] : [] };
  }, [docIds.join(',')]); // Use join to create a stable dependency string
  
  // Fetch documents for all docIds (matches + strong candidates + weak candidates) in a single API call
  // FindDocumentById will pause when ids is empty array (checked in pause logic)
  const { data: documentsData, fetching: documentsFetching, reexecuteQuery: refetchDocuments } = FindDocumentById(findDocumentVariables);
  
  // When appliedObligationIds change (meaning the backend processed recommendations and created/updated obligations),
  // refetch documents to get the updated data
  const [lastRefetchedIds, setLastRefetchedIds] = useState('');
  useEffect(() => {
    if (appliedObligationIds.length > 0) {
      const idsKey = appliedObligationIds.sort().join(',');
      if (idsKey !== lastRefetchedIds) {
        console.log('[ReconcileSideBar] AI recommendations processed, refetching documents...', appliedObligationIds);
        setLastRefetchedIds(idsKey);
        // Small delay to ensure backend has finished updating the obligation
        setTimeout(() => {
          refetchDocuments({ requestPolicy: 'network-only' });
        }, 300);
      }
    }
  }, [appliedObligationIds, lastRefetchedIds, refetchDocuments]);
  
  // NOTE:
  // urql will automatically execute when `findDocumentVariables` changes.
  // Avoid manually refetching here — it can create refetch loops if `reexecuteQuery`
  // changes identity or if this component re-renders frequently.
  
  // Map documents by id for quick lookup (soporta id y _id para evitar pérdida por formato)
  const documentsMap = documentsData?.findDocumentsById?.reduce((acc, doc) => {
    const id = doc.id ?? doc._id;
    if (id) {
      acc[String(id)] = doc;
      if (doc._id && String(doc._id) !== String(id)) acc[String(doc._id)] = doc;
    }
    return acc;
  }, {}) || {};

  // Helper para lookup seguro por id (soporta distintos formatos)
  const getDocumentById = (id) => documentsMap[String(id)] || documentsMap[id];
  
  // Humanize obligationType for display when we only have match data (e.g. document still loading)
  const obligationTypeToLabel = (type) => {
    if (!type || typeof type !== 'string') return 'Obligación';
    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
  };

  // Merge match data with document data.
  // When document is not in documentsMap (still loading, or deleted), show a minimal match so the UI
  // displays obligation type/description from the transaction match and does not show "no conciliado".
  const enrichedMatches = matchesFromTransaction
    .map(match => {
      const document = getDocumentById(match.docId);
      if (document) {
        const matchDescription = match.description ?? match.meta?.description ?? '';
        const docDescription = document.meta?.description ?? document.meta?.descriptionText ?? '';
        const description = matchDescription || docDescription;
        return {
          ...document,
          meta: {
            ...document.meta,
            creditDocumentId: document.meta?.creditDocumentId,
            parentCreditId: document.meta?.parentCreditId,
            description: description || document.meta?.description,
            descriptionText: description || document.meta?.descriptionText,
          },
          // Per-match journal entry state: only from this match. Do not fall back to document.entries.
          entries: match.entries ?? { completed: false, amount: 0, entryList: [] },
          matchedAmount: match.matchedAmount,
          userRelated: match.userRelated || document.userRelated,
          currency: match.currency || document.currencyType,
          currencyType: match.currency || document.currencyType,
          originalAmount:
            match.originalAmount ?? document.meta?.exchange?.originalAmount,
          exchangeRate:
            match.exchangeRate ?? document.meta?.exchange?.exchangeRate,
          localValue:
            match.localValue ?? document.meta?.exchange?.localAmount,
          matchCurrency: match.matchCurrency,
        };
      }
      // Fallback: document not loaded or not found — build minimal match so obligation type is visible
      const typeLabel = obligationTypeToLabel(match.obligationType);
      const desc = match.description ?? match.meta?.description ?? '';
      return {
        id: match.docId,
        docId: match.docId,
        documentType: 'OBLIGATION',
        meta: {
          type: match.obligationType,
          typeLabel,
          description: desc,
          descriptionText: desc,
        },
        amount: match.amount ?? match.originalAmount,
        currency: match.currency || 'CLP',
        currencyType: match.currency || 'CLP',
        entries: match.entries ?? { completed: false, amount: 0, entryList: [] },
        matchedAmount: match.matchedAmount,
        userRelated: match.userRelated,
        originalAmount: match.originalAmount,
        exchangeRate: match.exchangeRate,
        localValue: match.localValue,
        matchCurrency: match.matchCurrency,
      };
    });

  const { undoAll, isUndoing, job, percent, cancel, retry, reset } = useUndoAllMatches({
    editData,
    isDocument,
    companyId,
    matches: enrichedMatches,
  });
  const {
    summary: statementUnmatchSummary,
    setSummary: setStatementUnmatchSummary,
    findBlockedSummary,
    checking: checkingStatementUnmatch,
  } = useStatementUnmatchGuard(companyId);

  // Enrich strong match candidates with document data.
  // Exclude candidates whose document no longer exists (e.g. obligation was deleted) —
  // findDocumentsById does not return deleted documents, so they won't be in documentsMap.
  const enrichedStrongCandidates = strongMatchCandidates
    .map(candidate => {
      const document = getDocumentById(candidate.documentId);
      if (document) {
        return {
          ...document,
          amount: document.amount,
          currency: document.currencyType || document.currency || 'CLP',
          currencyType: document.currencyType || document.currency || 'CLP',
        };
      }
      return null;
    })
    .filter(Boolean);

  // Enrich weak match candidates; exclude deleted/missing documents.
  // Preserve source field to detect AI suggestions (MATCH_RECOMMENDER)
  const enrichedWeakCandidates = weakMatchCandidates
    .map(candidate => {
      const document = getDocumentById(candidate.documentId);
      if (document) {
        return {
          ...document,
          amount: document.amount,
          currency: document.currencyType || document.currency || 'CLP',
          currencyType: document.currencyType || document.currency || 'CLP',
          // Preserve source from document (MATCH_RECOMMENDER means AI suggestion)
          source: document.source,
          // Also check meta for AI suggestion flags
          isAISuggestion: document.source === 'MATCH_RECOMMENDER' || document.meta?.isAISuggestion,
        };
      }
      return null;
    })
    .filter(Boolean);

  // NOTA: aiRecommendations y systemRecommendations ya no se usan
  // Todo viene ahora de matchRecommendations (Zona de Entrenamiento)

  // Create AI weak candidates from applied_obligation_ids (Match Recommender created obligations)
  // These are obligations created automatically by the AI when the user opens a transaction
  const aiWeakCandidates = useMemo(() => {
    return appliedObligationIds
      .map(obligationId => {
        const document = getDocumentById(obligationId);
        if (document) {
          // Find the recommendation that created this obligation for confidence info
          const recommendation = matchRecommendations.find(r => r.applied_obligation_id === obligationId);
          return {
            ...document,
            amount: document.amount,
            currency: document.currencyType || document.currency || 'CLP',
            currencyType: document.currencyType || document.currency || 'CLP',
            source: 'MATCH_RECOMMENDER', // Mark as AI recommendation
            isAISuggestion: true,
            aiConfidenceScore: recommendation?.confidence_score,
            aiPatternId: recommendation?.pattern_hash,
            aiRecommendationId: recommendation?.id, // ID for rejecting the recommendation
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [appliedObligationIds, documentsMap, matchRecommendations]);

  // Combine weak candidates with AI weak candidates
  // AI candidates should appear first with purple styling
  const combinedWeakCandidates = useMemo(() => {
    // Get IDs of AI candidates to avoid duplicates
    const aiCandidateIds = new Set(aiWeakCandidates.map(c => c.id));
    // Filter out any weak candidates that are already in AI candidates
    const filteredWeakCandidates = enrichedWeakCandidates.filter(c => !aiCandidateIds.has(c.id));
    // AI candidates first, then regular weak candidates
    return [...aiWeakCandidates, ...filteredWeakCandidates];
  }, [aiWeakCandidates, enrichedWeakCandidates]);

  // Use server match data directly for MatchList (finds ALL match types: documents, transactions, manual, clay)
  // Fall back to enrichedMatches format if server data not yet loaded
  const matchsData = serverMatchData ? serverMatchData : (enrichedMatches.length > 0 ? {
    allMatchsByTransactionId: enrichedMatches,
    allMatchManualByTransactionId: [],
    allMatchByClay: [],
    allTransactionsByDocumentId: [],
  } : null);

  // Build match container for amount lookups
  // IMPORTANT: When displaying in CLP, use localValue when available (it is always in CLP).
  // This fixes USD/other-currency matches showing wrong amount with CLP label.
  const matchContainer = (isDocument ? editData?.meta?.matchs?.inMatch || [] : editData?.matchs)?.reduce(
    (acc, el) => {
      if (!acc) acc = {};
      const matchAmount = el.matchedAmount != null && el.matchedAmount !== undefined
        ? el.matchedAmount
        : (el.localValue || el.localAmount || el.amount || 0);
      const matchCurrency = el.matchCurrency || 'CLP';
      // When displaying in CLP, use localValue (or localAmount fallback) when available (currency conversion case)
      const clpAmount = el.localValue ?? el.localAmount;
      const displayAmount =
        matchCurrency === 'CLP' &&
        el.currency !== 'CLP' &&
        (clpAmount != null && clpAmount !== undefined)
          ? clpAmount
          : el.currency === 'CLP'
            ? matchAmount
            : (el.originalAmount ?? matchAmount);
      const key = el.matchId || el.id || el.docId || el.settleId;
      if (!key) return acc;
      acc[key] = displayAmount;
      acc[key + "currency"] = matchCurrency;
      acc[key + "matchedAmount"] = displayAmount;
      const docKey = el.id || el.docId || el.settleId;
      if (docKey && String(docKey) !== String(key) && acc[docKey] === undefined) {
        acc[docKey] = displayAmount;
        acc[docKey + "currency"] = matchCurrency;
        acc[docKey + "matchedAmount"] = displayAmount;
      }
      return acc;
    },
    {}
  ) || {};

  // Calculate reconciled amount (matchs sum when available; |amount|−|remaining| for legacy/negative amounts)
  const reconciledAmount = isDocument
    ? getReconciledAmount(editData?.amount, editData?.meta?.remaining, editData?.meta?.matchs?.inMatch)
    : getReconciledAmount(editData?.amount, editData?.remaining, matchesFromTransaction);

  const remainingForReconcile = isDocument
    ? editData?.meta?.remaining
    : editData?.remaining;
  const hasPendingBalance = hasPendingReconciliation(remainingForReconcile);
  const isFullyReconciledFlag = isDocument
    ? isFullyReconciled(editData?.amount, editData?.meta?.remaining, editData?.meta?.matchs?.inMatch)
    : isFullyReconciled(editData?.amount, editData?.remaining, matchesFromTransaction);

  const handleDownloadMatches = React.useCallback(() => {
    if (!editData) return;
    const currency = editData.currency || editData.currencyType || 'CLP';
    const docId = editData.id || editData._id || '';
    const fileName = `matchs-movimiento-${docId}-${moment().format('YYYY-MM-DD')}.xlsx`;

    const resolveConciliadoPor = (email) => {
      if (!email) return '-';
      const lower = String(email).toLowerCase();
      if (lower === 'bot@smartcfo.cl' || lower === 'sistema@smartcfo.cl') return 'Pascale';
      return email;
    };

    const matchSourceLabel = {
      manual:                        'Manual',
      automatic_rule:                'Regla automática',
      automatic_heuristic:           'Automático (heurística)',
      automatic_ai:                  'IA (Pascale)',
      automatic_n_1:                 'N abonos → 1 factura',
      automatic_ai_nomina:           'IA (nómina)',
      automatic_iva_postergado:      'IA (IVA postergado)',
      automatic_f29_check:           'IA (F29)',
      automatic_strong_candidate:    'Candidato fuerte',
      automatic_exact_candidates:    'Candidato exacto',
      confirmed_strong_candidate:    'Candidato fuerte (confirmado)',
      confirmed_ai_recommendation:   'Recomendación IA (confirmada)',
      automatic_credit:              'Crédito automático',
      auto_comision:                 'IA (comisión bancaria)',
      automatic_outsource:           'Match Externo',
    };

    const tipoLabel = editData.type === 'SPEND' ? 'Cargo' : (editData.type === 'RECIEVED' || editData.type === 'RECEIVED' ? 'Abono' : editData.type || '-');

    const movimientoRow = [{
      'Tipo':           tipoLabel,
      'Descripción':    editData.description || '-',
      'Fecha':          editData.date ? moment(editData.date).format('DD/MM/YYYY') : '-',
      'Moneda':         currency,
      'Monto':          editData.amount ?? '-',
      'Saldo':          editData.remaining ?? '-',
      'Conciliado':     reconciledAmount ?? '-',
      'Banco':          banksFormat[editData.bankId] || editData.bankId || '-',
      'N° Cuenta':      editData.accountNumber || '-',
    }];

    // Combine all available server match arrays + enrichedMatches fallback
    const serverMatches = [
      ...(serverMatchData?.allTransactionsByDocumentId || []),
      ...(serverMatchData?.allMatchsByTransactionId || []),
    ];
    const manualMatches = [
      ...(serverMatchData?.allMatchManualByTransactionId || []),
      ...(serverMatchData?.allMatchByClay || []),
    ].map(m => ({
      ...m,
      id: m.settleId || m.id,
      documentType: null,
    }));
    const allMatchesForExport = serverMatches.length > 0
      ? [...serverMatches, ...manualMatches]
      : enrichedMatches;

    const matchRows = allMatchesForExport.map(m => {
      const { matchedByEmail, matchCreationDate, matchSource } = getMatchUserInfo(m, editData);
      const isAccounted = Boolean(m.entries?.completed || m.transaction?.entries?.completed);
      const eoafCategory = m.cashflowArea?.name || '-';
      const docLabel = getDocumentLabelWithNumber(m) || '-';
      const descripcion = getName(m) || m.description || '-';
      const fechaMovimiento = m.documentDate
        ? moment(m.documentDate).format('DD/MM/YYYY')
        : m.date ? moment(m.date).format('DD/MM/YYYY') : '-';
      const fechaConciliacion = matchCreationDate
        ? moment(matchCreationDate).format('DD/MM/YYYY HH:mm')
        : '-';

      return {
        'Movimiento / Documento': docLabel,
        'Descripción':            descripcion,
        'Fecha movimiento':       fechaMovimiento,
        'Fecha conciliación':     fechaConciliacion,
        'Moneda':                 m.currency || m.currencyType || m.matchCurrency || 'CLP',
        'Monto conciliado':       m.matchedAmount ?? m.amount ?? '-',
        'Monto original':         m.originalAmount ?? '-',
        'Valor local (CLP)':      m.localValue ?? '-',
        'Tipo de cambio':         m.exchangeRate ?? '-',
        'Conciliado por':         resolveConciliadoPor(matchedByEmail),
        'Método':                 matchSourceLabel[matchSource] || matchSource || 'Manual',
        'Contabilizado':          isAccounted ? 'Sí' : 'No',
        'Categoría EOAF':         eoafCategory,
        'Emisor':                 m.meta?.issuerName || m.issuerName || '-',
        'RUT Emisor':             m.meta?.issuerId || m.issuerId || '-',
        'Receptor':               m.meta?.receiverName || m.receiverName || '-',
        'RUT Receptor':           m.meta?.receiverId || m.receiverId || '-',
      };
    });

    import('xlsx').then(XLSX => {
      const wb = XLSX.utils.book_new();
      const wsMatch = XLSX.utils.json_to_sheet(
        matchRows.length
          ? matchRows
          : [{ 'Sin matchs': 'Este movimiento no tiene matchs registrados' }]
      );
      const matchColCount = matchRows.length > 0 ? Object.keys(matchRows[0]).length : 17;
      wsMatch['!cols'] = Array.from({ length: matchColCount }, (_, i) => {
        if (i === 0) return { wch: 38 };
        if (i === 1) return { wch: 45 };
        if (i === 9) return { wch: 25 }; // Conciliado por
        return { wch: 16 };
      });
      const wsMovimiento = XLSX.utils.json_to_sheet(movimientoRow);
      const movColCount = Object.keys(movimientoRow[0]).length;
      wsMovimiento['!cols'] = [{ wch: 20 }, { wch: 45 }, ...Array(Math.max(0, movColCount - 2)).fill({ wch: 16 })];
      XLSX.utils.book_append_sheet(wb, wsMatch, 'Matchs');
      XLSX.utils.book_append_sheet(wb, wsMovimiento, 'Movimiento');
      XLSX.writeFile(wb, fileName);
    });
  }, [editData, enrichedMatches, serverMatchData, reconciledAmount]);

  // Guard: el wrapper en reconcile.jsx puede renderizar este sidebar cuando `data` (estado local)
  // existe pero `openSidebar.editData` quedó en null durante una transición. Sin editData no hay nada
  // que mostrar; evitamos el crash "Cannot read properties of null (reading 'amount')".
  if (!editData) return null;

  return (
    <div className="reconcileForm">
      <TransactionInfoBar editData={editData} />
      
      {/* Action Buttons and Conciliado Amount */}
      <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
        <div className="tw-text-sm tw-text-gray-700 d-flex align-items-center">
          <span className="tw-font-semibold">{dt('reconcile.reconciledLabel')}</span>
          <span style={{ marginLeft: '4px' }}>{formatMoneyWithCurrency(reconciledAmount, editData.currency)}</span>
        </div>
        <div className="d-flex agendaSubmitButton align-items-center" style={{ gap: '0.5em' }}>
          <DocumentActionsDropdown
            onDownloadMatches={handleDownloadMatches}
            onUndoMatches={
              !isReadOnly && enrichedMatches.length > 0
                ? () => setShowUndoAllModal(true)
                : undefined
            }
          />
          {/* Solo usuarios restringidos o con conciliación bloqueada no ven botones de acción */}
          {!isReadOnly && (
            <>
              {!crearObligacionBlocked && (
              <Button
                type="button"
                size="mini"
                onClick={() => setShowObligationModal(true)}
                disabled={!hasPendingBalance}
              >
                {dt('reconcile.closeWithObligation')}
              </Button>
              )}
              <Button
                type="button"
                size="mini"
                className="scfo-btn-conciliar-primary"
                onClick={() => setShowVincularModal(true)}
                disabled={isFullyReconciledFlag}
              >
                {dt('reconcile.reconcile')}
                <i className="arrow up right icon" style={{ marginLeft: '0.5em', transform: 'rotate(45deg)' }}></i>
              </Button>
            </>
          )}
        </div>
      </div>
      
      {/* Horizontal Divider */}
      <Divider className="mb-4" />
      
      {/* Matches List - key por transacción para que al cambiar de fila se monte de nuevo y muestre las recomendaciones correctas */}
      <MatchList
        key={isDocument ? `doc-${editData?.id ?? editData?._id}` : `tx-${transactionId}`}
        matches={matchsData}
        matchContainer={matchContainer}
        editData={editData}
        isTransitioning={false}
        fetching={matchesFetching || (documentsFetching && docIds.length > 0)}
        history={history}
        location={location}
        strongCandidates={!isDocument ? enrichedStrongCandidates : []}
        weakCandidates={!isDocument ? combinedWeakCandidates : []}
        matchRecommendations={!isDocument ? matchRecommendations : []}
        onMatchRecommendationRejected={() => {
          refetchMatchRec({ requestPolicy: 'network-only' });
        }}
        onMatchRecommendationApplied={() => {
          // Devolver la promesa para que el componente que aplica pueda esperar al refetch y la UI se actualice
          return refetchMatchRec({ requestPolicy: 'network-only' });
        }}
        readOnly={isReadOnly}
      />

      {/* Modals */}
      <VincularDocumentoModal
        open={showVincularModal}
        onClose={() => setShowVincularModal(false)}
        data={editData}
      />

      <CreateObligationModal
        open={showObligationModal && !crearObligacionBlocked}
        onClose={() => setShowObligationModal(false)}
        initialData={editData}
      />

      <CustomModal
        open={showUndoAllModal}
        onClose={() => { if (!isUndoing) setShowUndoAllModal(false); }}
        title={dt('reconcile.undoAllMatchesTitle')}
        size="small"
        dimmer="blurring"
        closeOnDimmerClick={!isUndoing}
        closeOnEscape={!isUndoing}
        classNameModal="delete-obligation-modal"
        classNameContent="delete-obligation-modal-content"
        content={<UndoAllMatchesModalContent count={enrichedMatches.length} />}
        onConfirm={async () => {
          const pairs = (enrichedMatches || [])
            .map((match) => buildUnmatchInput(match, editData, isDocument, companyId))
            .filter(Boolean)
            .map((input) => ({ originId: input.originId, endId: input.endId }));
          const blocked = await findBlockedSummary(pairs);
          if (blocked) {
            setShowUndoAllModal(false);
            setStatementUnmatchSummary(blocked);
            return;
          }
          await undoAll();
          setShowUndoAllModal(false);
        }}
        confirmLabel={dt('reconcile.undoAllMatchesConfirm')}
        confirmDisabled={isUndoing || checkingStatementUnmatch}
        isLoading={isUndoing || checkingStatementUnmatch}
        confirmVariant="danger"
        cancelarLabel={dt('common.cancel')}
      />

      <StatementLinkedUnmatchModal
        open={Boolean(statementUnmatchSummary)}
        summary={statementUnmatchSummary}
        onClose={() => setStatementUnmatchSummary(null)}
      />

      <BulkUnmatchProgress
        job={job}
        percent={percent}
        onCancel={cancel}
        onRetry={retry}
        onClose={reset}
      />

      {/* Report Button - Only for transactions */}
      {!isDocument && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '12px 16px', borderTop: '1px solid #e0e0e0', marginTop: '16px' }}>
          <PossibleDuplicateReviewControls data={editData} />
          <ReportDocumentButton data={editData} documentType="TRANSACTION" isTransaction />
        </div>
      )}
    </div>
  );
};

export default ReconcileSideBar;